// Landing Page — ProcesoFácil
// Diseño: 100% dark theme, paleta emerald/teal/cyan, glass morphism
import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Loading } from '../components/ui/loading';
import { cn } from '../lib/utils';
import {
  Zap,
  Clock,
  MousePointerClick,
  Play,
  ArrowRight,
  CheckCircle2,
  MessageCircle,
  Mail,
  Monitor,
  FileSpreadsheet,
  Repeat,
  AlertTriangle,
  Timer,
  TrendingUp,
  Menu,
  X,
  Keyboard,
  ChevronDown,
  ChevronRight,
  Globe,
  AppWindow,
  Sparkles,
  Shield,
} from 'lucide-react';

// =============================================
// Estilos de botón para <Link> y <a>
// Evita anidamiento inválido <a><button>
// =============================================
const btn = {
  base: 'inline-flex items-center justify-center rounded-lg font-semibold transition-all duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950',
  primary: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg shadow-emerald-500/20',
  outline: 'border border-white/10 hover:border-emerald-500/30 hover:bg-white/5 text-gray-300 hover:text-white',
  ghostLight: 'hover:bg-white/8 text-gray-400 hover:text-white',
  sm: 'text-sm h-9 px-4',
  lg: 'text-base h-12 px-8',
};

// =============================================
// Hook: scroll reveal con IntersectionObserver
// Compatible Edge 109+
// =============================================
function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

function ScrollReveal({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={cn(
        'transition-all duration-700 ease-out',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
        className
      )}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

// =============================================
// Logo — siempre sobre fondo oscuro
// =============================================
function Logo({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const styles = {
    sm: { icon: 'h-4 w-4', pad: 'p-1', text: 'text-lg' },
    md: { icon: 'h-5 w-5', pad: 'p-1.5', text: 'text-xl' },
    lg: { icon: 'h-8 w-8', pad: 'p-2.5', text: 'text-3xl' },
  }[size];

  return (
    <span className="flex items-center gap-2.5">
      <span className={`rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 ${styles.pad} inline-flex shadow-lg shadow-emerald-500/20`}>
        <Zap className={`${styles.icon} text-white`} />
      </span>
      <span className={cn(styles.text, 'font-extrabold tracking-tight text-white')}>
        Proceso<span className="text-gradient">Fácil</span>
      </span>
    </span>
  );
}

// =============================================
// Navbar con menú hamburger — glass effect oscuro
// =============================================
function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    { href: '#como-funciona', label: 'Cómo funciona' },
    { href: '#precios', label: 'Precios' },
    { href: '#contacto', label: 'Contacto' },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-gray-950/80"
      style={{ backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
      aria-label="Navegación principal"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" aria-label="ProcesoFácil - Inicio">
          <Logo size="md" />
        </Link>

        {/* Links desktop */}
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-400">
          {navLinks.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              className="hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-sm transition-colors"
            >
              {label}
            </a>
          ))}
        </div>

        {/* Botones desktop + hamburger mobile */}
        <div className="flex items-center gap-3">
          <Link to="/login" className={cn(btn.base, btn.sm, btn.ghostLight, 'hidden sm:inline-flex')}>
            Iniciar sesión
          </Link>
          <Link to="/register" className={cn(btn.base, btn.sm, btn.primary, 'hidden sm:inline-flex btn-glow')}>
            Registrarse gratis
          </Link>

          {/* Hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Panel mobile */}
      {menuOpen && (
        <div className="md:hidden border-t border-white/5 bg-gray-950/95 px-4 py-4 space-y-1">
          {navLinks.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              onClick={() => setMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              {label}
            </a>
          ))}
          <div className="pt-3 border-t border-white/5 flex flex-col gap-2">
            <Link to="/login" onClick={() => setMenuOpen(false)} className={cn(btn.base, btn.sm, btn.ghostLight, 'w-full justify-center')}>
              Iniciar sesión
            </Link>
            <Link to="/register" onClick={() => setMenuOpen(false)} className={cn(btn.base, btn.sm, btn.primary, 'w-full justify-center')}>
              Registrarse gratis
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

// =============================================
// Mockup simulado del Editor de Workflows
// 100% CSS — sin imágenes
// =============================================
function EditorMockup({ glow = false }: { glow?: boolean }) {
  return (
    <div className={cn(
      'relative w-full rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50',
      glow && 'shadow-emerald-500/5'
    )}>
      {/* Barra del "navegador" */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 border-b border-white/5">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
        </div>
        <div className="flex-1 mx-6">
          <div className="bg-gray-800 rounded-md px-3 py-1 text-[10px] text-gray-500 text-center max-w-52 mx-auto truncate">
            procesofacil.com/editor
          </div>
        </div>
      </div>

      {/* Layout del editor */}
      <div className="flex bg-gray-950">
        {/* Mini sidebar */}
        <div className="w-10 shrink-0 border-r border-white/5 py-3 hidden sm:flex flex-col items-center gap-2.5">
          <div className="w-5 h-5 rounded bg-emerald-500/20 flex items-center justify-center">
            <Zap className="w-3 h-3 text-emerald-400" />
          </div>
          <div className="w-5 h-5 rounded bg-white/5" />
          <div className="w-5 h-5 rounded bg-white/5" />
          <div className="mt-auto w-5 h-5 rounded bg-white/5" />
        </div>

        {/* Canvas con nodos */}
        <div className="flex-1 relative py-5 px-4 sm:px-6 min-h-[300px] bg-[radial-gradient(circle,rgba(16,185,129,0.03)_1px,transparent_1px)] [background-size:16px_16px]">
          <div className="flex flex-col items-center gap-0">
            {/* Nodo Inicio */}
            <div className="px-4 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 text-[10px] font-semibold flex items-center gap-1.5 shadow-sm shadow-emerald-500/10">
              <Play className="w-2.5 h-2.5 fill-current" /> Inicio
            </div>
            <div className="flex justify-center"><div className="flow-line" /></div>

            {/* Nodo acción: Abrir sistema */}
            <div className="w-48 rounded-lg bg-gray-800/80 border border-white/8 px-3 py-2 flex items-center gap-2.5 shadow-sm">
              <div className="w-6 h-6 rounded-md bg-teal-500/15 flex items-center justify-center shrink-0">
                <AppWindow className="w-3.5 h-3.5 text-teal-400" />
              </div>
              <div>
                <div className="text-[10px] text-gray-300 font-medium">Abrir Sistema</div>
                <div className="text-[9px] text-gray-500">SIAF - Módulo RRHH</div>
              </div>
            </div>
            <div className="flex justify-center"><div className="flow-line" /></div>

            {/* Nodo acción: Navegar */}
            <div className="w-48 rounded-lg bg-gray-800/80 border border-white/8 px-3 py-2 flex items-center gap-2.5 shadow-sm">
              <div className="w-6 h-6 rounded-md bg-cyan-500/15 flex items-center justify-center shrink-0">
                <Globe className="w-3.5 h-3.5 text-cyan-400" />
              </div>
              <div>
                <div className="text-[10px] text-gray-300 font-medium">Navegar a</div>
                <div className="text-[9px] text-gray-500 truncate">siaf.gob.pe/registro</div>
              </div>
            </div>
            <div className="flex justify-center"><div className="flow-line" /></div>

            {/* Nodo Loop */}
            <div className="w-56 rounded-xl bg-emerald-500/5 border border-emerald-500/15 p-3 shadow-sm shadow-emerald-500/5">
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-5 h-5 rounded-md bg-emerald-500/15 flex items-center justify-center">
                  <Repeat className="w-3 h-3 text-emerald-400" />
                </div>
                <span className="text-[10px] text-emerald-300 font-semibold">Loop: planilla.xlsx</span>
                <span className="text-[9px] text-emerald-500/80 ml-auto font-medium">200 filas</span>
              </div>

              <div className="space-y-0 ml-2 pl-2.5 border-l border-emerald-500/15">
                {/* Hijo: escribir */}
                <div className="w-full rounded-md bg-gray-800/80 border border-white/6 px-2.5 py-1.5 flex items-center gap-2">
                  <Keyboard className="w-3 h-3 text-amber-400 shrink-0" />
                  <span className="text-[10px] text-gray-300">
                    Escribir <code className="text-emerald-400 font-mono">{'{{fila.DNI}}'}</code>
                  </span>
                </div>
                <div className="flex justify-center"><div className="flow-line h-3" /></div>
                {/* Hijo: click */}
                <div className="w-full rounded-md bg-gray-800/80 border border-white/6 px-2.5 py-1.5 flex items-center gap-2">
                  <MousePointerClick className="w-3 h-3 text-teal-400 shrink-0" />
                  <span className="text-[10px] text-gray-300">Click &quot;Guardar&quot;</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Panel de propiedades */}
        <div className="w-44 shrink-0 border-l border-white/5 p-3 bg-gray-900/50 hidden lg:block">
          <div className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-3">Propiedades</div>
          <div className="space-y-2.5">
            <div>
              <div className="text-[9px] text-gray-600 mb-1">Tipo</div>
              <div className="bg-gray-800 rounded px-2 py-1 text-[10px] text-gray-400 flex items-center justify-between">
                Escribir texto <ChevronDown className="w-2.5 h-2.5" />
              </div>
            </div>
            <div>
              <div className="text-[9px] text-gray-600 mb-1">Texto</div>
              <div className="bg-gray-800 rounded px-2 py-1 text-[10px] text-emerald-400 font-mono">
                {'{{fila.DNI}}'}
              </div>
            </div>
            <div>
              <div className="text-[9px] text-gray-600 mb-1">Selector</div>
              <div className="bg-gray-800 rounded px-2 py-1 text-[10px] text-gray-500 font-mono truncate">
                #txt_documento
              </div>
            </div>
            <div className="pt-1">
              <div className="w-full bg-emerald-600/80 rounded px-2 py-1 text-[10px] text-white font-medium text-center">
                Guardar
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================
// Hero — oscuro, dramático, emerald mesh
// =============================================
function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gray-950" aria-labelledby="hero-heading">
      {/* Fondo mesh gradient */}
      <div className="absolute inset-0 mesh-gradient" />
      <div className="absolute inset-0 dot-pattern opacity-40" />

      {/* Orbes decorativos que "respiran" */}
      <div className="absolute top-20 -left-40 w-[500px] h-[500px] rounded-full bg-emerald-500/8 blur-3xl animate-breathe" />
      <div className="absolute -bottom-20 -right-40 w-[400px] h-[400px] rounded-full bg-teal-500/6 blur-3xl animate-breathe" style={{ animationDelay: '4s' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 lg:pt-32 lg:pb-28 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Texto — izquierda */}
          <div className="flex-1 max-w-2xl text-center lg:text-left">
            {/* Badge */}
            <div className="animate-fade-in-up inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-sm font-medium text-emerald-300 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" style={{ animationDuration: '2s' }} />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Automatización visual — sin código
            </div>

            {/* Título */}
            <h1
              id="hero-heading"
              className="animate-fade-in-up text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight text-white leading-[1.08] mb-6"
              style={{ animationDelay: '100ms' }}
            >
              Libera horas de{' '}
              <span className="text-gradient-animated">trabajo manual</span>
              <br className="hidden sm:block" />
              {' '}cada mes
            </h1>

            {/* Subtítulo */}
            <p
              className="animate-fade-in-up text-lg sm:text-xl text-gray-400 max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed"
              style={{ animationDelay: '200ms' }}
            >
              ¿Tu equipo repite los mismos pasos en el sistema todos los meses?
              ProcesoFácil automatiza esas tareas repetitivas con un editor visual,
              sin necesidad de saber programar.
            </p>

            {/* CTAs */}
            <div className="animate-fade-in-up flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start" style={{ animationDelay: '300ms' }}>
              <Link to="/register" className={cn(btn.base, btn.lg, btn.primary, 'btn-glow')}>
                Comenzar gratis
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <a href="#como-funciona" className={cn(btn.base, btn.lg, btn.ghostLight)}>
                Ver cómo funciona
                <ChevronDown className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Mockup del editor — derecha */}
          <div
            className="animate-fade-in-up flex-1 w-full max-w-2xl lg:max-w-none"
            style={{ animationDelay: '400ms' }}
          >
            {/* Glow detrás del mockup */}
            <div className="relative">
              <div className="absolute -inset-4 bg-emerald-500/5 blur-2xl rounded-3xl" />
              <div className="relative">
                <EditorMockup glow />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================
// Stats bar — glass card sobre fondo oscuro
// =============================================
function StatsBar() {
  return (
    <div className="relative z-10 -mt-8 mb-0 px-4">
      <ScrollReveal>
        <div
          className="max-w-4xl mx-auto rounded-2xl border border-white/8 shadow-2xl shadow-black/30 px-6 py-8 sm:px-10 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center bg-gray-900/60"
          style={{ backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
        >
          {[
            { value: '10×', label: 'más rápido que hacerlo manual', color: 'text-emerald-400' },
            { value: '0', label: 'líneas de código necesarias', color: 'text-teal-400' },
            { value: '80%', label: 'menos errores de digitación', color: 'text-cyan-400' },
          ].map(({ value, label, color }) => (
            <div key={label}>
              <div className={`text-4xl sm:text-5xl font-extrabold ${color} mb-1 tabular-nums`}>{value}</div>
              <div className="text-sm text-gray-500 font-medium">{label}</div>
            </div>
          ))}
        </div>
      </ScrollReveal>
    </div>
  );
}

// =============================================
// Problema — oscuro con tinte rose
// =============================================
function ProblemSection() {
  // Anchos pre-calculados para las barras de esqueleto (evita Math.random en render)
  const barWidths = [
    { main: '85%', sub: '55%' },
    { main: '92%', sub: '48%' },
    { main: '78%', sub: '62%' },
    { main: '95%', sub: '42%' },
    { main: '82%', sub: '58%' },
  ];

  return (
    <section className="relative py-24 sm:py-32 px-4 bg-gray-950 overflow-hidden" aria-labelledby="problem-heading">
      {/* Tinte rose sutil */}
      <div className="absolute inset-0 mesh-gradient-rose" />

      <div className="relative max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Texto narrativo */}
          <ScrollReveal className="flex-1 max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-semibold uppercase tracking-wider mb-6">
              <AlertTriangle className="h-3.5 w-3.5" />
              El problema
            </div>

            <h2 id="problem-heading" className="text-3xl sm:text-4xl font-extrabold text-white leading-tight mb-6">
              Tu equipo pierde horas
              <span className="block text-rose-400">en trabajo que una máquina haría en minutos</span>
            </h2>

            <p className="text-gray-400 leading-relaxed mb-8 text-lg">
              Cada mes, el personal administrativo lee datos de documentos, planillas o archivos
              y los digita uno por uno en el sistema — un formulario tras otro, un registro tras otro.
              Es tedioso, consume horas, y los errores de digitación son inevitables.
            </p>

            <div className="space-y-4">
              {[
                { icon: Timer, text: 'Horas de trabajo repetitivo cada mes' },
                { icon: Repeat, text: 'Los mismos pasos, los mismos formularios, una y otra vez' },
                { icon: AlertTriangle, text: 'Errores de digitación que causan problemas después' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3 text-gray-300">
                  <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-rose-500/10 border border-rose-500/15 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-rose-400" />
                  </div>
                  <span className="text-sm font-medium">{text}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>

          {/* Visual del problema — glass card */}
          <ScrollReveal className="flex-1 w-full max-w-lg" delay={150}>
            <div className="relative">
              <div
                className="rounded-2xl border border-white/8 p-6 shadow-2xl shadow-black/30 bg-gray-900/60"
                style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
              >
                <div className="text-xs font-semibold text-rose-400 uppercase tracking-wider mb-4">Proceso actual</div>

                <div className="space-y-3">
                  {barWidths.map((widths, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="h-7 w-7 rounded-full bg-white/5 border border-white/8 flex items-center justify-center text-xs font-bold text-gray-500">
                        {i + 1}
                      </div>
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2 rounded-full bg-white/8" style={{ width: widths.main }} />
                        <div className="h-2 rounded-full bg-white/4" style={{ width: widths.sub }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-dashed border-white/8 flex items-center justify-center gap-2 text-gray-500">
                  <Repeat className="h-4 w-4" />
                  <span className="text-xs font-medium">× 200 registros cada mes...</span>
                </div>
              </div>

              {/* Badge de tiempo con glow */}
              <div className="absolute -bottom-4 -right-4 bg-rose-500 text-white rounded-xl px-4 py-2 shadow-lg shadow-rose-500/30 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-bold">+8 horas</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}

// =============================================
// Solución — glass cards con acentos de color
// =============================================
function SolutionSection() {
  const features = [
    {
      icon: MousePointerClick,
      title: 'Editor visual de arrastrar y soltar',
      description: 'Crea tu proceso conectando bloques de acciones. Sin escribir código, sin manuales técnicos.',
      accent: 'emerald',
      iconBg: 'bg-emerald-500/10 border-emerald-500/20',
      iconColor: 'text-emerald-400',
      hoverBorder: 'hover:border-emerald-500/25',
      hoverGlow: 'hover:shadow-emerald-500/5',
      topLine: 'via-emerald-500/40',
    },
    {
      icon: FileSpreadsheet,
      title: 'Carga masiva desde Excel o CSV',
      description: 'Sube tu archivo y el sistema ingresa cada fila automáticamente en la aplicación.',
      accent: 'teal',
      iconBg: 'bg-teal-500/10 border-teal-500/20',
      iconColor: 'text-teal-400',
      hoverBorder: 'hover:border-teal-500/25',
      hoverGlow: 'hover:shadow-teal-500/5',
      topLine: 'via-teal-500/40',
    },
    {
      icon: Monitor,
      title: 'Compatible con cualquier sistema',
      description: 'Funciona con aplicaciones de escritorio (incluso legacy) y páginas web. No importa la antigüedad del sistema.',
      accent: 'cyan',
      iconBg: 'bg-cyan-500/10 border-cyan-500/20',
      iconColor: 'text-cyan-400',
      hoverBorder: 'hover:border-cyan-500/25',
      hoverGlow: 'hover:shadow-cyan-500/5',
      topLine: 'via-cyan-500/40',
    },
    {
      icon: Play,
      title: 'Ejecuta y observa en tiempo real',
      description: 'Presiona un botón y mira cómo cada paso se completa solo. Progreso visible en todo momento.',
      accent: 'amber',
      iconBg: 'bg-amber-500/10 border-amber-500/20',
      iconColor: 'text-amber-400',
      hoverBorder: 'hover:border-amber-500/25',
      hoverGlow: 'hover:shadow-amber-500/5',
      topLine: 'via-amber-500/40',
    },
  ];

  return (
    <section className="relative py-24 sm:py-32 px-4 bg-gray-950 overflow-hidden" aria-labelledby="solution-heading">
      <div className="absolute inset-0 grid-pattern" />

      <div className="relative max-w-6xl mx-auto">
        <ScrollReveal className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="h-3.5 w-3.5" />
            La solución
          </div>
          <h2 id="solution-heading" className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
            Automatiza sin programar
          </h2>
          <p className="text-lg text-gray-400 max-w-xl mx-auto">
            Diseña el proceso una vez, ejecútalo cada mes sin errores.
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {features.map((feature, i) => (
            <ScrollReveal key={feature.title} delay={i * 100}>
              <div className={cn(
                'group relative rounded-2xl p-6 sm:p-7 overflow-hidden h-full transition-all duration-300',
                'bg-white/[0.02] border border-white/[0.06]',
                feature.hoverBorder,
                'hover:bg-white/[0.04] hover:shadow-xl',
                feature.hoverGlow
              )}>
                {/* Línea de acento superior */}
                <div className={cn(
                  'absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent to-transparent',
                  feature.topLine
                )} />

                <div className="flex gap-4">
                  <div className={cn(
                    'flex-shrink-0 h-12 w-12 rounded-xl border flex items-center justify-center transition-shadow duration-300',
                    feature.iconBg,
                    'group-hover:shadow-lg',
                    feature.hoverGlow
                  )}>
                    <feature.icon className={cn('h-6 w-6', feature.iconColor)} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Diferenciador */}
        <ScrollReveal className="mt-12 flex justify-center" delay={400}>
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white/[0.03] border border-white/8">
            <Shield className="h-5 w-5 text-gray-500" />
            <span className="text-sm text-gray-400">
              Sin código. Sin programadores. Solo <span className="font-semibold text-white">tu equipo y ProcesoFácil.</span>
            </span>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

// =============================================
// Mockup: Panel de propiedades (paso 2) — oscuro
// =============================================
function PropertiesMockup() {
  return (
    <div className="rounded-2xl bg-gray-900/80 border border-white/8 overflow-hidden shadow-2xl shadow-black/30">
      <div className="px-5 py-3 border-b border-white/5 bg-gray-900">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Propiedades del paso</div>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <div className="text-[11px] text-gray-500 mb-1.5 font-medium">Tipo de acción</div>
          <div className="bg-gray-800/80 border border-white/5 rounded-lg px-3 py-2 text-sm text-gray-300 flex items-center justify-between">
            Escribir texto <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
          </div>
        </div>
        <div>
          <div className="text-[11px] text-gray-500 mb-1.5 font-medium">Texto a escribir</div>
          <div className="bg-gray-800/80 border border-white/5 rounded-lg px-3 py-2 text-sm font-mono">
            <span className="text-emerald-400">{'{{fila.DNI}}'}</span>
          </div>
        </div>
        <div>
          <div className="text-[11px] text-gray-500 mb-1.5 font-medium">Selector del campo</div>
          <div className="bg-gray-800/80 border border-white/5 rounded-lg px-3 py-2 text-sm text-gray-500 font-mono truncate">
            #txt_documento
          </div>
        </div>
        <div className="pt-2">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg px-4 py-2.5 text-sm font-semibold text-center cursor-default">
            Guardar configuración
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================
// Mockup: Progreso de ejecución (paso 3) — oscuro
// =============================================
function ExecutionMockup() {
  const steps = [
    { step: 'Abrir Sistema SIAF', status: 'done' as const },
    { step: 'Navegar a "Registro"', status: 'done' as const },
    { step: 'Escribir DNI: 45678912', status: 'done' as const },
    { step: 'Click "Buscar"', status: 'current' as const },
    { step: 'Escribir monto: 1,500.00', status: 'pending' as const },
  ];

  return (
    <div className="rounded-2xl bg-gray-900/80 border border-white/8 overflow-hidden shadow-2xl shadow-black/30">
      {/* Barra de progreso */}
      <div className="px-5 py-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-xs font-semibold text-gray-300 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-soft-pulse" />
            Ejecutando workflow...
          </span>
          <span className="text-xs text-emerald-400 font-mono font-semibold">145/200</span>
        </div>
        <div className="w-full h-2.5 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
            style={{ width: '72%' }}
          />
        </div>
      </div>

      {/* Log de pasos */}
      <div className="p-4 space-y-1.5">
        {steps.map(({ step, status }) => (
          <div
            key={step}
            className={cn(
              'flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium',
              status === 'done' && 'text-gray-500',
              status === 'current' && 'bg-emerald-500/10 border border-emerald-500/15 text-emerald-300',
              status === 'pending' && 'text-gray-600',
            )}
          >
            {status === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
            {status === 'current' && <div className="w-3.5 h-3.5 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin shrink-0" />}
            {status === 'pending' && <div className="w-3.5 h-3.5 rounded-full border border-gray-700 shrink-0" />}
            {step}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================
// Cómo Funciona — con mockups y timeline
// =============================================
function HowItWorksSection() {
  const steps = [
    {
      number: '01',
      title: 'Diseña tu proceso',
      description: 'Usa el editor visual para armar tu flujo. Arrastra bloques como "hacer clic", "escribir texto", "esperar" y conéctalos en el orden que necesites.',
      mockup: <EditorMockup />,
    },
    {
      number: '02',
      title: 'Configura cada paso',
      description: 'Selecciona cada bloque y configúralo: qué botón presionar, qué texto escribir, de qué columna de Excel leer. Todo desde un panel visual, sin código.',
      mockup: <PropertiesMockup />,
    },
    {
      number: '03',
      title: 'Ejecuta y observa',
      description: 'Presiona "Ejecutar" y el sistema realiza todo el proceso automáticamente. Ve el progreso en tiempo real mientras se completa cada paso.',
      mockup: <ExecutionMockup />,
    },
  ];

  return (
    <section id="como-funciona" className="py-24 sm:py-32 px-4 scroll-mt-16 bg-gray-950" aria-labelledby="how-heading">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal className="text-center mb-20">
          <h2 id="how-heading" className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
            Tres pasos. Sin complicaciones.
          </h2>
          <p className="text-lg text-gray-400">
            Así de simple es dejar de hacer trabajo manual
          </p>
        </ScrollReveal>

        <div className="space-y-24 sm:space-y-28">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className={cn(
                'flex flex-col items-center gap-10 md:gap-16',
                index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'
              )}
            >
              {/* Texto */}
              <ScrollReveal className="flex-1 max-w-md">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-4">
                  Paso {step.number}
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {step.description}
                </p>
              </ScrollReveal>

              {/* Mockup con glow sutil */}
              <ScrollReveal className="flex-1 w-full max-w-xl" delay={150}>
                <div className="relative">
                  <div className="absolute -inset-2 bg-emerald-500/3 blur-xl rounded-3xl" />
                  <div className="relative">
                    {step.mockup}
                  </div>
                </div>
              </ScrollReveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================
// Caso de Uso — oscuro con emerald mesh
// =============================================
function UseCaseSection() {
  return (
    <section className="relative py-24 sm:py-32 px-4 overflow-hidden bg-gray-950" aria-labelledby="usecase-heading">
      {/* Fondo gradiente emerald sutil */}
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/30 via-gray-950 to-gray-950" />
      <div className="absolute inset-0 mesh-gradient opacity-60" />
      <div className="absolute inset-0 dot-pattern opacity-30" />

      <div className="relative max-w-4xl mx-auto">
        <ScrollReveal className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-6">
            <TrendingUp className="h-3.5 w-3.5" />
            Caso de uso principal
          </div>
          <h2 id="usecase-heading" className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
            Carga masiva de datos en sistemas
            <span className="block text-emerald-400">que no lo permiten</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Muchas entidades públicas y empresas trabajan con sistemas que no
            permiten cargas masivas. El personal lee datos de documentos en mano y
            los digita uno por uno — provocando errores y consumiendo horas.
            Quienes usan archivos Excel o CSV reducen errores de lectura, pero el
            trabajo repetitivo de ingresar fila por fila sigue siendo igual de tedioso.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {[
              { text: 'Organiza tus datos en un archivo Excel o CSV', icon: FileSpreadsheet },
              { text: 'El sistema abre la aplicación por ti', icon: AppWindow },
              { text: 'Llena cada formulario automáticamente', icon: Keyboard },
              { text: 'Cero errores de digitación, en minutos', icon: CheckCircle2 },
            ].map((item) => (
              <div
                key={item.text}
                className="flex items-center gap-3 rounded-xl px-5 py-4 bg-white/[0.03] border border-white/[0.06] transition-all duration-200 hover:bg-white/[0.05] hover:border-emerald-500/20"
              >
                <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <item.icon className="h-4 w-4 text-emerald-400" />
                </div>
                <span className="text-sm font-medium text-gray-300">{item.text}</span>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

// =============================================
// Precios — glass cards oscuras
// =============================================
function PricingSection() {
  return (
    <section id="precios" className="py-24 sm:py-32 px-4 scroll-mt-16 bg-gray-950" aria-labelledby="pricing-heading">
      <div className="max-w-4xl mx-auto">
        <ScrollReveal className="text-center mb-12">
          <h2 id="pricing-heading" className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
            La plataforma es gratis
          </h2>
          <p className="text-lg text-gray-400 max-w-xl mx-auto">
            Regístrate, explora y usa ProcesoFácil sin costo.
            Solo pagas si necesitas que nuestro equipo cree el flujo por ti.
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Plan GRATIS */}
          <ScrollReveal>
            <div className="relative rounded-2xl border border-white/[0.06] overflow-hidden bg-white/[0.02] h-full transition-all duration-300 hover:border-emerald-500/20 hover:bg-white/[0.04]">
              <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
              <div className="p-7 sm:p-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-4">
                  Siempre gratis
                </div>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-extrabold text-white">S/ 0</span>
                </div>

                <div className="space-y-3 mb-8">
                  {[
                    'Crear tu cuenta sin límites',
                    'Acceso completo al editor visual',
                    'Crear y ejecutar tus propios flujos',
                    'Soporte para Excel y CSV',
                    'Compatible con sistemas de escritorio y web',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>

                <Link to="/register" className={cn(btn.base, btn.outline, 'w-full h-11 text-sm')}>
                  Crear cuenta gratis
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </ScrollReveal>

          {/* Plan SERVICIO */}
          <ScrollReveal delay={150}>
            <div className="relative rounded-2xl border border-amber-500/20 overflow-hidden bg-white/[0.03] h-full transition-all duration-300 hover:border-amber-500/30 hover:bg-white/[0.05] shadow-lg shadow-amber-500/5">
              <div className="h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
              <div className="p-7 sm:p-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-4">
                  Creación de flujos
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-extrabold text-white">S/ 120</span>
                  <span className="text-sm text-gray-500 font-medium">desde</span>
                </div>
                <p className="text-sm text-gray-400 mb-6">
                  ¿Prefieres que lo hagamos nosotros? Creamos tu flujo de trabajo a medida.
                </p>

                {/* Escala de precios */}
                <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 mb-6">
                  <div className="space-y-2.5">
                    {[
                      { nodes: '1–20 pasos', price: 'S/ 120' },
                      { nodes: '21–40 pasos', price: 'S/ 200' },
                      { nodes: '41–60 pasos', price: 'S/ 300' },
                      { nodes: '61–80 pasos', price: 'S/ 400' },
                    ].map((tier) => (
                      <div key={tier.nodes} className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">{tier.nodes}</span>
                        <span className="font-semibold text-white tabular-nums">{tier.price}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-white/5">
                    +S/ 100 por cada 20 pasos adicionales
                  </p>
                </div>

                <div className="space-y-3 mb-8">
                  {[
                    'Creamos el flujo completo por ti',
                    'Asesoría para identificar qué automatizar',
                    'Soporte directo por WhatsApp',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>

                <a href="#contacto" className={cn(btn.base, 'w-full h-11 text-sm bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white shadow-lg shadow-amber-500/20')}>
                  Solicitar asesoría
                  <MessageCircle className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}

// =============================================
// Contacto — glass cards oscuras
// =============================================
function ContactSection() {
  const whatsappNumber = '51948924822';
  const whatsappMessage = encodeURIComponent('Hola, me interesa saber más sobre ProcesoFácil');
  const email = 'edwinwmendez@gmail.com';

  return (
    <section id="contacto" className="relative py-24 sm:py-32 px-4 bg-gray-950 scroll-mt-16 overflow-hidden" aria-labelledby="contact-heading">
      <div className="absolute inset-0 grid-pattern opacity-50" />

      <div className="relative max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <ScrollReveal className="flex-1">
            <h2 id="contact-heading" className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
              ¿Necesitas ayuda para identificar
              <span className="text-gradient"> qué automatizar?</span>
            </h2>
            <p className="text-gray-400 leading-relaxed text-lg">
              Escríbenos por WhatsApp o correo. Te ayudamos a identificar qué
              procesos repetitivos puedes automatizar en tu entidad o empresa.
              Sin compromiso.
            </p>
          </ScrollReveal>

          <ScrollReveal className="flex-1 w-full max-w-sm space-y-4" delay={150}>
            <a
              href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              className="card-hover group flex items-center gap-4 px-6 py-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] transition-all duration-300 hover:border-emerald-500/25 hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950"
            >
              <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-emerald-500/10 transition-shadow">
                <MessageCircle className="h-6 w-6 text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">WhatsApp</div>
                <div className="text-sm text-gray-400">948 924 822</div>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-600 ml-auto group-hover:text-gray-400 transition-colors" />
            </a>

            <a
              href={`mailto:${email}?subject=Consulta sobre ProcesoFácil`}
              className="card-hover group flex items-center gap-4 px-6 py-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] transition-all duration-300 hover:border-cyan-500/25 hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950"
            >
              <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-cyan-500/10 transition-shadow">
                <Mail className="h-6 w-6 text-cyan-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">Correo electrónico</div>
                <div className="text-sm text-gray-400">{email}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-600 ml-auto group-hover:text-gray-400 transition-colors" />
            </a>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}

// =============================================
// CTA Final — dramático, oscuro con emerald mesh
// =============================================
function FinalCTASection() {
  return (
    <section className="py-24 sm:py-28 px-4 relative overflow-hidden bg-gray-950" aria-labelledby="cta-heading">
      <div className="absolute inset-0 mesh-gradient" />
      <div className="absolute inset-0 dot-pattern opacity-20" />

      {/* Orbe decorativo */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-emerald-500/6 blur-3xl rounded-full" />

      <ScrollReveal className="relative max-w-2xl mx-auto text-center">
        <h2 id="cta-heading" className="text-3xl sm:text-4xl font-extrabold text-white mb-4 leading-tight">
          Cada mes que pasa, tu equipo repite
          <span className="block text-gradient-animated">las mismas horas de trabajo manual.</span>
        </h2>
        <p className="text-lg text-gray-400 max-w-xl mx-auto mb-10">
          Crea tu cuenta gratuita y descubre cómo automatizar esos procesos.
        </p>
        <Link to="/register" className={cn(btn.base, btn.lg, btn.primary, 'btn-glow text-base px-10')}>
          Comenzar gratis — crear cuenta
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </ScrollReveal>
    </section>
  );
}

// =============================================
// Footer
// =============================================
function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-8 px-4 border-t border-white/5 bg-gray-950">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Logo size="sm" />
          <span className="text-sm text-gray-500">
            © {currentYear}
          </span>
        </div>
        <div className="flex items-center gap-6 text-sm text-gray-500">
          <Link
            to="/terms"
            className="hover:text-gray-300 focus-visible:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-sm transition-colors"
          >
            Términos
          </Link>
          <Link
            to="/privacy"
            className="hover:text-gray-300 focus-visible:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-sm transition-colors"
          >
            Privacidad
          </Link>
          <a
            href="#contacto"
            className="hover:text-gray-300 focus-visible:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-sm transition-colors"
          >
            Contacto
          </a>
        </div>
      </div>
    </footer>
  );
}

// =============================================
// Landing Page Principal
// =============================================
export default function LandingPage() {
  const { user, initialized } = useAuthStore();

  // Si Firebase aún verifica la sesión, mostrar loading con branding
  if (!initialized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-950">
        <Logo size="lg" />
        <div className="mt-6">
          <Loading size="lg" />
        </div>
      </div>
    );
  }

  // Usuario autenticado → redirigir directo al dashboard (sin flash)
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Skip link para accesibilidad — WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-emerald-500 focus:text-white focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-semibold"
      >
        Saltar al contenido principal
      </a>

      <Navbar />

      <main id="main-content">
        <HeroSection />
        <StatsBar />
        <ProblemSection />
        <SolutionSection />
        <HowItWorksSection />
        <UseCaseSection />
        <PricingSection />
        <ContactSection />
        <FinalCTASection />
      </main>

      <Footer />
    </div>
  );
}
